import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowUturnLeftIcon, EyeIcon, MagnifyingGlassIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { saleReversalService } from '../../services/saleReversalService'
import { saleService } from '../../services/saleService'
import dayjs from 'dayjs'

export function SaleReversalsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')

  // Reverse modal state
  const [showReverseModal, setShowReverseModal] = useState(false)
  const [selectedSale, setSelectedSale] = useState<any>(null)
  const [reason, setReason] = useState('')

  // Sale search state
  const [saleSearch, setSaleSearch] = useState('')
  const [debouncedSaleSearch, setDebouncedSaleSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const { data: reversalsData, isLoading } = useQuery({
    queryKey: ['sale-reversals'],
    queryFn: () => saleReversalService.getAll(),
  })

  // Debounce sale number search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSaleSearch(saleSearch)
    }, 400)
    return () => clearTimeout(timer)
  }, [saleSearch])

  const { data: salesData, isLoading: loadingSales } = useQuery({
    queryKey: ['sales-for-reversal', debouncedSaleSearch, startDate, endDate],
    queryFn: () =>
      saleService.getAll({
        status: 'confirmed,paid,partially_paid',
        search: debouncedSaleSearch || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: 50,
      }),
    enabled: showReverseModal,
  })

  const reverseMutation = useMutation({
    mutationFn: saleReversalService.reverseSale,
    onSuccess: () => {
      toast.success('Sale reversed successfully')
      queryClient.invalidateQueries({ queryKey: ['sale-reversals'] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      closeReverseModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reverse sale')
    },
  })

  const closeReverseModal = useCallback(() => {
    setShowReverseModal(false)
    setSelectedSale(null)
    setReason('')
    setSaleSearch('')
    setDebouncedSaleSearch('')
    setStartDate('')
    setEndDate('')
  }, [])

  const handleReverse = () => {
    if (!selectedSale || !reason.trim()) {
      toast.error('Please select a sale and provide a reason')
      return
    }
    reverseMutation.mutate({ sale_id: selectedSale.sale_id, reason: reason.trim() })
  }

  const reversals = reversalsData?.data?.data?.items || []
  const sales = salesData?.data?.data?.items || []

  const filteredReversals = reversals.filter((r: any) => {
    const term = searchTerm.toLowerCase()
    return (
      r.sales?.sale_number?.toLowerCase().includes(term) ||
      r.sales?.customers?.company_name?.toLowerCase().includes(term) ||
      r.sales?.customers?.first_name?.toLowerCase().includes(term) ||
      r.reason?.toLowerCase().includes(term)
    )
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sale Reversals</h1>
          <p className="text-gray-500 mt-1">Reverse sales and restore stock</p>
        </div>
        <button
          onClick={() => setShowReverseModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <ArrowUturnLeftIcon className="w-5 h-5" />
          Reverse Sale
        </button>
      </div>

      {/* Search reversals */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search reversals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Reversals Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reversed By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </td>
              </tr>
            ) : filteredReversals.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No reversals found
                </td>
              </tr>
            ) : (
              filteredReversals.map((reversal: any) => (
                <tr key={reversal.reversal_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {reversal.sales?.sale_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {reversal.sales?.customers?.company_name ||
                     `${reversal.sales?.customers?.first_name} ${reversal.sales?.customers?.last_name}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    KSh {Number(reversal.sales?.total_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {reversal.reason}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {reversal.users?.first_name} {reversal.users?.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(reversal.reversed_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => navigate(`/sales/${reversal.sales?.sale_id}`)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Reverse Sale Modal */}
      {showReverseModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={closeReverseModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Reverse Sale</h2>

              {/* Search Filters */}
              <div className="space-y-4 mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by sale number..."
                    value={saleSearch}
                    onChange={(e) => setSaleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      placeholder="Start date"
                    />
                  </div>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      placeholder="End date"
                    />
                  </div>
                </div>
              </div>

              {/* Sale Results */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                  {loadingSales ? 'Searching...' : `${sales.length} sale${sales.length !== 1 ? 's' : ''} found`}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {loadingSales ? (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
                    </div>
                  ) : sales.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">
                      {debouncedSaleSearch || startDate || endDate
                        ? 'No sales match your search'
                        : 'Start typing a sale number or pick a date range'}
                    </p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {sales.map((sale: any) => (
                        <button
                          key={sale.sale_id}
                          type="button"
                          onClick={() => setSelectedSale(sale)}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            selectedSale?.sale_id === sale.sale_id ? 'bg-primary-50 border-l-4 border-primary-500' : 'border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {sale.sale_number}
                                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                  {sale.status}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500">
                                {sale.customers?.company_name || `${sale.customers?.first_name || ''} ${sale.customers?.last_name || ''}`.trim() || 'Walk-in'}
                                {' · '}
                                {dayjs(sale.sale_date).format('MMM D, YYYY')}
                              </p>
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              KSh {Number(sale.total_amount).toLocaleString()}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Sale Summary */}
              {selectedSale && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm space-y-1">
                  <p><strong>Sale:</strong> {selectedSale.sale_number}</p>
                  <p><strong>Customer:</strong> {selectedSale.customers?.company_name || 'Walk-in'}</p>
                  <p><strong>Amount:</strong> KSh {Number(selectedSale.total_amount).toLocaleString()}</p>
                  <p><strong>Items:</strong> {selectedSale.sale_items?.length || 0}</p>
                  <p><strong>Date:</strong> {dayjs(selectedSale.sale_date).format('MMM D, YYYY')}</p>
                </div>
              )}

              {/* Reason */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Reversal *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter reason for reversing this sale..."
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={closeReverseModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReverse}
                  disabled={reverseMutation.isPending || !selectedSale || !reason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reverseMutation.isPending ? 'Reversing...' : 'Reverse Sale'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
